import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../api/client";
import {
  confirmUploaded,
  useImport,
  usePatchImport,
  usePublishImport,
  useRejectImport,
} from "../api/imports";
import type {
  ImportDetail,
  ImportDraft,
  ImportDraftSlot,
  ImportPatch,
} from "../api/types";
import { ERROR_COPY, rowMessageFor, warningFor } from "../copy/errors";
import { ErrorLine } from "../ui/ErrorLine";
import { formatDuration } from "../ui/format";
import { useNow } from "../ui/useNow";
import { NotFoundPage } from "../pages/NotFoundPage";
import {
  Button,
  Card,
  Field,
  ImageLightbox,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from "../ui";
import { humanise, sizeCm } from "../viewer/math";
import { Trail } from "./Breadcrumb";
import { categoryWord } from "./categories";
import { ImportSlotCard } from "./ImportSlotCard";
import { SectionHeader } from "./SectionHeader";

const EYEBROW = "— Objects";
export const REVIEW_COPY = {
  waiting: "Waiting for the file…",
  checking: "Checking your model… this usually takes under a minute.",
  converting: "Converting and checking your model…",
  slow: "Large models can take up to five minutes. You can leave this page; it will be waiting in Objects.",
  pausedTitle: "Waiting to be checked…",
  paused:
    "Checks are paused on our side. Your model will be checked when they resume; you can leave this page. If it is not checked within 30 minutes, it stops here with a message, and nothing is charged.",
  nothingAdded: "Nothing was added to Objects.",
  realSize: "If that is not its real size, set Real height below.",
  published: "This model is in Objects.",
  rejected: "This import was discarded.",
  discard: "Discard this import? The uploaded file is deleted.",
  applyFirst: "Apply your changes first",
  heightFirst: "Set the real height first",
  keeps: "Ads already made keep the version they used.",
} as const;
/** After this long a check is a large model's. */
const SLOW_AFTER_S = 90;

const TOP_KEYS = [
  "name",
  "description",
  "tags",
  "category",
  "height_m",
  "front",
  "mount",
  "replaces",
] as const;
const SLOT_KEYS = [
  "rename",
  "use",
  "accepts",
  "material",
  "reunwrap",
  "baked",
] as const;
/** The fields of the form a refusal can be shown beside. */
const PLACED = [
  "name",
  "description",
  "category",
  "tags",
  "height_m",
  "mount",
  "front",
];
const MOUNTS: { id: ImportDraft["mount"]; label: string }[] = [
  { id: "floor", label: "On the floor or a table" },
  { id: "wall", label: "On a wall" },
  { id: "ceiling", label: "Hanging from the ceiling" },
];
const FRONTS: ImportDraft["front"][] = ["-y", "+x", "+y", "-x"];

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

/** What **Apply changes** sends: the top-level keys that differ, and for each
 *  surface that differs its name and only its changed keys. */
export function draftChanges(
  base: ImportDraft,
  local: ImportDraft,
): ImportPatch {
  const patch: Record<string, unknown> = {};
  for (const key of TOP_KEYS) {
    if (!same(base[key], local[key])) patch[key] = local[key];
  }
  const slots: Record<string, unknown>[] = [];
  for (const slot of local.slots) {
    const was = base.slots.find((s) => s.name === slot.name);
    if (!was) continue;
    const entry: Record<string, unknown> = {};
    for (const key of SLOT_KEYS) {
      if (!same(was[key], slot[key])) entry[key] = slot[key];
    }
    if (Object.keys(entry).length > 0)
      slots.push({ name: slot.name, ...entry });
  }
  if (slots.length > 0) patch.slots = slots;
  return patch as ImportPatch;
}

/** The tags field's text as tags: words separated by commas or spaces. */
export const tagsOf = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[\s,]+/)
    .filter(Boolean);

const heightText = (height_m: number | null) =>
  height_m === null ? "" : String(Number((height_m * 100).toFixed(4)));

/** The file's name without its extension. */
function stem(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

const categoryLabel = categoryWord;

const ACTION =
  "min-h-[44px] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0";
/** A button of the footer: three of them share one row of a phone. */
const FOOT = `${ACTION} !px-3 !text-[13px] sm:!px-[18px] sm:!text-sm`;
/** A link in a sentence, tall enough for a thumb on a phone. */
const INLINE_LINK =
  "inline-flex min-h-[44px] items-center underline sm:min-h-0";
const HEADING = "m-0 text-lg font-semibold text-slate-900";
const LINE = "m-0 text-sm text-slate-700";
const RADIO_ROW =
  "flex min-h-[44px] items-center gap-2 text-sm text-slate-800 sm:min-h-0";

/** The field a refused **Apply changes** names: a detail of the model, or a
 *  surface (by its name) of the patch that was sent. */
type Invalid = { field: string } | { slot: string } | null;

function invalidOf(error: unknown, sent: ImportPatch | null): Invalid {
  if (!(error instanceof ApiError) || error.code !== "validation_error") {
    return null;
  }
  const detail = (error.detail ?? {}) as { field?: unknown };
  const field = typeof detail.field === "string" ? detail.field : "";
  const slot = /^slots\[(\d+)\]/.exec(field);
  if (slot) {
    const name = sent?.slots?.[Number(slot[1])]?.name;
    return name ? { slot: name } : { field: "" };
  }
  return { field };
}

/** The pictures and the facts at the top of a model that has been checked. */
function Summary(props: {
  detail: ImportDetail;
  name: string;
  onOpenPicture(src: string, alt: string): void;
}) {
  const { detail } = props;
  const report = detail.report;
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
      {detail.thumb_url ? (
        <button
          type="button"
          className="block w-full max-w-xs shrink-0 cursor-zoom-in overflow-hidden rounded-lg border-0 bg-slate-100 p-0"
          onClick={() =>
            props.onOpenPicture(detail.thumb_url ?? "", props.name)
          }
        >
          <img
            src={detail.thumb_url}
            alt={props.name}
            width={512}
            height={512}
            className="block aspect-square h-auto w-full object-cover"
          />
        </button>
      ) : null}
      {report ? (
        <div className="flex min-w-0 flex-col gap-2">
          <p className={LINE} data-testid="size-line">
            {sizeCm(report.size_m)}. {REVIEW_COPY.realSize}
          </p>
          <details className="text-sm text-slate-700">
            <summary className="inline-flex min-h-[44px] cursor-pointer items-center underline sm:min-h-0">
              More
            </summary>
            <p className="m-0 mt-1">
              Detail: {report.triangles.toLocaleString("en-US")} triangles
            </p>
          </details>
        </div>
      ) : null}
    </div>
  );
}

/** The form of a model in review, and the same form, dimmed and locked,
 *  behind the line of a model that is being checked again. */
function ReviewForm(props: {
  detail: ImportDetail;
  draft: ImportDraft;
  local: ImportDraft;
  setLocal(next: ImportDraft): void;
  texts: Texts;
  setTexts(next: Texts): void;
  invalid: Invalid;
  locked: boolean;
  onOpenPicture(src: string, alt: string): void;
}) {
  const { detail, draft, local, texts, invalid } = props;
  const report = detail.report;
  if (!report) return null;
  const change = (changes: Partial<ImportDraft>) =>
    props.setLocal({ ...local, ...changes });
  const changeSlot = (name: string, changes: Partial<ImportDraftSlot>) =>
    change({
      slots: local.slots.map((slot) =>
        slot.name === name ? { ...slot, ...changes } : slot,
      ),
    });
  const refused = (field: string) =>
    invalid && "field" in invalid && invalid.field === field
      ? ERROR_COPY.validation_error
      : null;
  const duplicate = report.duplicate_of;
  const warnings = report.warnings
    .filter((w) => w.code !== "uv_overlap" && w.code !== "thumb_fallback")
    .map(warningFor)
    .filter((sentence): sentence is string => sentence !== null);
  const categories = detail.categories.includes(local.category)
    ? detail.categories
    : [...detail.categories, local.category];

  return (
    <fieldset
      disabled={props.locked}
      className={`m-0 flex min-w-0 flex-col gap-6 border-0 p-0 ${props.locked ? "opacity-50" : ""}`}
      data-testid="review-form"
    >
      <Summary
        detail={detail}
        name={draft.name}
        onOpenPicture={props.onOpenPicture}
      />

      {warnings.length > 0 ? (
        <ul
          className="m-0 flex list-none flex-col gap-1 p-0"
          aria-label="What the check noted"
        >
          {warnings.map((sentence) => (
            <li key={sentence} className={LINE}>
              {sentence}
            </li>
          ))}
        </ul>
      ) : null}

      {duplicate ? (
        <div className="flex flex-col gap-2" data-testid="duplicate">
          <p className={LINE}>
            {local.replaces === duplicate.id
              ? `Replaces ${duplicate.name}. ${REVIEW_COPY.keeps}`
              : `You already imported this file as ${duplicate.name}.`}
          </p>
          {refused("replaces") ? (
            <p role="alert" className="tb-field-error m-0">
              {refused("replaces")}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={
                local.replaces === duplicate.id ? "primary" : "secondary"
              }
              className={ACTION}
              aria-pressed={local.replaces === duplicate.id}
              onClick={() => change({ replaces: duplicate.id })}
            >
              Replace it
            </Button>
            <Button
              type="button"
              size="sm"
              variant={local.replaces === null ? "primary" : "secondary"}
              className={ACTION}
              aria-pressed={local.replaces === null}
              onClick={() => change({ replaces: null })}
            >
              Keep both
            </Button>
          </div>
        </div>
      ) : null}

      <section aria-labelledby="about-it">
        <Card className="flex flex-col gap-4 p-4 sm:p-6">
          <h2 id="about-it" className={HEADING}>
            About it
          </h2>
          <Field label="Name" error={refused("name")}>
            <Input
              maxLength={80}
              className="min-h-[44px] sm:min-h-0"
              value={local.name}
              onChange={(event) => change({ name: event.target.value })}
            />
          </Field>
          <Field label="Description" error={refused("description")}>
            <Input
              maxLength={200}
              className="min-h-[44px] sm:min-h-0"
              value={local.description}
              onChange={(event) => change({ description: event.target.value })}
            />
          </Field>
          <Field label="Category" error={refused("category")}>
            <Select
              className="min-h-[44px] sm:min-h-0"
              value={local.category}
              onChange={(event) => change({ category: event.target.value })}
            >
              {categories.map((id) => (
                <option key={id} value={id}>
                  {categoryLabel(id)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tags" error={refused("tags")}>
            <Input
              className="min-h-[44px] sm:min-h-0"
              value={texts.tags}
              onChange={(event) => {
                props.setTexts({ ...texts, tags: event.target.value });
                change({ tags: tagsOf(event.target.value) });
              }}
            />
          </Field>
        </Card>
      </section>

      <section aria-labelledby="size-and-position">
        <Card className="flex flex-col gap-4 p-4 sm:p-6">
          <h2 id="size-and-position" className={HEADING}>
            Size and position
          </h2>
          <Field
            label="Real height (cm)"
            error={refused("height_m")}
            className="max-w-xs"
          >
            <Input
              type="number"
              inputMode="decimal"
              min={0.5}
              max={5000}
              step="any"
              className="min-h-[44px] sm:min-h-0"
              value={texts.height}
              onChange={(event) => {
                const text = event.target.value;
                const cm = Number(text);
                props.setTexts({ ...texts, height: text });
                change({
                  height_m:
                    text.trim() !== "" && Number.isFinite(cm) && cm > 0
                      ? cm / 100
                      : draft.height_m,
                });
              }}
            />
          </Field>
          <fieldset className="m-0 flex min-w-0 flex-col gap-1 border-0 p-0">
            <legend className="tb-field-label mb-1 p-0">
              Where does it go?
            </legend>
            {MOUNTS.map((mount) => (
              <label key={mount.id} className={RADIO_ROW}>
                <input
                  type="radio"
                  name="import-mount"
                  checked={local.mount === mount.id}
                  onChange={() => change({ mount: mount.id })}
                />
                {mount.label}
              </label>
            ))}
            {refused("mount") ? (
              <p role="alert" className="tb-field-error m-0">
                {refused("mount")}
              </p>
            ) : null}
          </fieldset>
          <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
            <legend className="tb-field-label mb-1 p-0">
              Which side is the front?
            </legend>
            <div className="grid max-w-xl grid-cols-2 gap-3 sm:grid-cols-4">
              {FRONTS.map((front, index) => {
                const alt = `Side ${index + 1}`;
                const url =
                  detail.front_views.find((view) => view.front === front)
                    ?.url ?? null;
                const pressed = local.front === front;
                return (
                  <button
                    key={front}
                    type="button"
                    aria-pressed={pressed}
                    className={`block overflow-hidden rounded-lg bg-slate-100 p-0 ${
                      pressed
                        ? "border-2 border-brand outline outline-2 outline-brand"
                        : "border-2 border-transparent"
                    }`}
                    onClick={() => change({ front })}
                  >
                    {url ? (
                      <img
                        src={url}
                        alt={alt}
                        className="block aspect-square w-full object-cover"
                      />
                    ) : (
                      <span className="flex aspect-square w-full items-center justify-center bg-slate-200 text-xs text-slate-600">
                        {alt}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {refused("front") ? (
              <p role="alert" className="tb-field-error m-0">
                {refused("front")}
              </p>
            ) : null}
          </fieldset>
        </Card>
      </section>

      <section aria-labelledby="surfaces" className="flex flex-col gap-4">
        <h2 id="surfaces" className={HEADING}>
          Surfaces
        </h2>
        {local.slots.map((slot) => {
          const fact = report.slots.find((s) => s.name === slot.name);
          if (!fact) return null;
          const applied = draft.slots.find((s) => s.name === slot.name);
          return (
            <ImportSlotCard
              key={slot.name}
              slot={slot}
              fact={fact}
              label={humanise(applied?.rename ?? slot.name)}
              checkUrl={
                detail.uv_checks.find((check) => check.slot === slot.name)
                  ?.url ?? null
              }
              materials={detail.materials}
              nameText={texts.names[slot.name] ?? humanise(slot.rename)}
              onNameText={(text) =>
                props.setTexts({
                  ...texts,
                  names: { ...texts.names, [slot.name]: text },
                })
              }
              onChange={(changes) => changeSlot(slot.name, changes)}
              invalid={
                invalid !== null &&
                "slot" in invalid &&
                invalid.slot === slot.name
              }
              onOpenPicture={props.onOpenPicture}
            />
          );
        })}
      </section>
    </fieldset>
  );
}

/** What the fields that are typed as text hold, beside the draft they edit. */
interface Texts {
  tags: string;
  height: string;
  /** source slot name -> the Name field's text */
  names: Record<string, string>;
}

const textsOf = (draft: ImportDraft): Texts => ({
  tags: draft.tags.join(", "),
  height: heightText(draft.height_m),
  names: Object.fromEntries(
    draft.slots.map((slot) => [slot.name, humanise(slot.rename)]),
  ),
});

/** `/assets/imports/:id`: one model on its way into the 3D library. While it
 *  is checked the page waits; once it is checked the member corrects what the
 *  check could not know (its real height, its front, what each surface is)
 *  and adds it. Nothing is sent until **Apply changes**. */
export function ImportReviewPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const query = useImport(id);
  const { refetch } = query;
  const patch = usePatchImport(id);
  const publish = usePublishImport(id);
  const reject = useRejectImport(id);
  const detail = query.data;
  const state = detail?.state;
  const draft = detail?.draft ?? null;

  // The local copy follows the server's draft whenever that changes: on the
  // first answer, after a change of the details, and when a check ends.
  const draftKey = draft ? JSON.stringify(draft) : "";
  const [local, setLocal] = useState<ImportDraft | null>(null);
  const [texts, setTexts] = useState<Texts | null>(null);
  useEffect(() => {
    const next = draftKey ? (JSON.parse(draftKey) as ImportDraft) : null;
    setLocal(next);
    setTexts(next ? textsOf(next) : null);
  }, [draftKey]);

  const [saved, setSaved] = useState(false);
  const sent = useRef<ImportPatch | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(
    null,
  );

  // A model whose file may have arrived while nobody was looking: say so
  // once. A refusal means it has not, or the hour is full; the page waits.
  const confirmed = useRef(false);
  useEffect(() => {
    if (state !== "uploaded" || confirmed.current) return;
    confirmed.current = true;
    confirmUploaded(id)
      .then(() => refetch())
      .catch(() => undefined);
  }, [state, id, refetch]);

  // The elapsed time of a check, from when the import entered the state (the
  // server's time): a page opened an hour later says an hour.
  const now = useNow(1000, state === "checking");

  if (query.isPending) {
    return (
      <>
        <PageHeader eyebrow={EYEBROW} />
        <SectionHeader />
        <div
          className="flex max-w-3xl flex-col gap-4"
          aria-busy="true"
          data-testid="import-skeleton"
        >
          <div className="aspect-square w-full max-w-xs overflow-hidden rounded-lg">
            <Skeleton width="100%" height="100%" />
          </div>
          <Skeleton width="100%" height={44} />
          <Skeleton width="100%" height={44} />
          <Skeleton width="100%" height={44} />
        </div>
      </>
    );
  }
  if (query.isError || !detail) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return <NotFoundPage />;
    }
    return (
      <>
        <PageHeader eyebrow={EYEBROW} />
        <SectionHeader />
        <ErrorLine error={query.error} context="import" />
      </>
    );
  }

  const name = draft?.name ?? stem(detail.name);
  const changes = draft && local ? draftChanges(draft, local) : {};
  const dirty = Object.keys(changes).length > 0;
  const needsHeight =
    (detail.report?.warnings ?? []).some((w) => w.code === "set_real_height") &&
    draft?.height_m === null;
  const busy = patch.isPending || publish.isPending || reject.isPending;
  const invalid = patch.isError ? invalidOf(patch.error, sent.current) : null;
  // A refused field the form shows the sentence beside; anything else (the
  // hour's limit, a field the form does not have) is said above the buttons.
  const placed =
    invalid !== null &&
    ("slot" in invalid ||
      PLACED.includes(invalid.field) ||
      (invalid.field === "replaces" && Boolean(detail.report?.duplicate_of)));
  const open = (src: string, alt: string) => setLightbox({ src, alt });

  const apply = () => {
    if (!dirty || busy) return;
    sent.current = changes;
    setSaved(false);
    publish.reset();
    patch.mutate(changes, {
      onSuccess: (reply) => setSaved(reply.state === "review"),
    });
  };
  const add = () => {
    if (dirty || needsHeight || busy) return;
    setSaved(false);
    patch.reset();
    publish.mutate(undefined, {
      onSuccess: (made) =>
        navigate(`/assets/shape/${encodeURIComponent(made.id)}`, {
          state: { added: true },
        }),
      onError: (error) => {
        if (error instanceof ApiError && error.code === "invalid_state") {
          void refetch();
        }
      },
    });
  };
  const discard = () => {
    if (!window.confirm(REVIEW_COPY.discard)) return;
    reject.mutate(undefined, {
      onSuccess: () => navigate("/assets"),
    });
  };
  const discardButton = (
    <Button
      type="button"
      variant="ghost"
      className={state === "review" ? FOOT : ACTION}
      disabled={reject.isPending}
      onClick={discard}
    >
      Discard
    </Button>
  );
  const edit = (next: ImportDraft) => {
    setLocal(next);
    setSaved(false);
  };

  const since = Date.parse(detail.updated_at);
  const elapsed = Number.isNaN(since) ? 0 : Math.max(0, (now - since) / 1000);
  const paused = detail.waiting_reason === "paused";
  const form =
    draft && local && texts && detail.report ? (
      <ReviewForm
        detail={detail}
        draft={draft}
        local={local}
        setLocal={edit}
        texts={texts}
        setTexts={setTexts}
        invalid={invalid}
        locked={state !== "review" || busy}
        onOpenPicture={open}
      />
    ) : null;

  return (
    <>
      <PageHeader eyebrow={EYEBROW} title={name} />
      <SectionHeader />
      <Trail
        steps={[
          { text: "Objects", to: "/assets" },
          { text: "Import", to: "/assets/imports" },
        ]}
        here={name}
      />

      <div className="flex max-w-3xl flex-col gap-6">
        {state === "uploaded" ? (
          <>
            <p role="status" className={LINE}>
              {REVIEW_COPY.waiting}
            </p>
            <div>{discardButton}</div>
          </>
        ) : null}

        {state === "checking" ? (
          <>
            <div className="flex flex-col gap-2">
              <p
                role="status"
                className="m-0 text-sm font-semibold text-slate-800"
              >
                {paused
                  ? REVIEW_COPY.pausedTitle
                  : /\.glb$/i.test(detail.name)
                    ? REVIEW_COPY.checking
                    : REVIEW_COPY.converting}{" "}
                <span className="font-normal tabular-nums text-slate-600">
                  {formatDuration(elapsed)}
                </span>
              </p>
              {paused ? (
                <p className={LINE}>{REVIEW_COPY.paused}</p>
              ) : elapsed > SLOW_AFTER_S ? (
                <p className={LINE}>{REVIEW_COPY.slow}</p>
              ) : null}
            </div>
            <div>{discardButton}</div>
            {form}
          </>
        ) : null}

        {state === "failed" ? (
          <>
            <Summary detail={detail} name={name} onOpenPicture={open} />
            <p role="alert" className={LINE}>
              {rowMessageFor(detail.error_code, detail.error_params)}
            </p>
            <p className={LINE}>{REVIEW_COPY.nothingAdded}</p>
            <div className="flex flex-wrap gap-3">
              <Button
                as={Link}
                to="/assets/import"
                variant="primary"
                className="min-h-[44px] sm:min-h-0"
              >
                Upload a different file
              </Button>
              {discardButton}
            </div>
          </>
        ) : null}

        {state === "review" ? (
          <>
            {detail.error_code ? (
              <p
                role="alert"
                className="m-0 text-sm font-semibold text-[color:var(--color-error)]"
                data-testid="recheck-failed"
              >
                {rowMessageFor(detail.error_code, detail.error_params)}
              </p>
            ) : null}
            {form}
            <div
              className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-2 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none"
              data-testid="review-footer"
            >
              {saved ? (
                <p role="status" className={LINE}>
                  Saved.
                </p>
              ) : null}
              {patch.isError && !placed ? (
                <ErrorLine error={patch.error} context="import" />
              ) : null}
              {publish.isError ? (
                <ErrorLine error={publish.error} context="import" />
              ) : null}
              {reject.isError ? (
                <ErrorLine error={reject.error} context="import" />
              ) : null}
              <div className="flex flex-wrap gap-x-1.5 gap-y-2 whitespace-nowrap sm:gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  className={FOOT}
                  disabled={!dirty || busy}
                  onClick={apply}
                >
                  Apply changes
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className={FOOT}
                  disabled={dirty || needsHeight || busy}
                  title={
                    dirty
                      ? REVIEW_COPY.applyFirst
                      : needsHeight
                        ? REVIEW_COPY.heightFirst
                        : undefined
                  }
                  onClick={add}
                >
                  Add to Objects
                </Button>
                {discardButton}
              </div>
            </div>
          </>
        ) : null}

        {state === "published" ? (
          <p className={LINE}>
            {REVIEW_COPY.published}{" "}
            {detail.published ? (
              <Link
                to={`/assets/shape/${encodeURIComponent(detail.published.id)}`}
                className={INLINE_LINK}
              >
                Open it
              </Link>
            ) : null}
          </p>
        ) : null}

        {state === "rejected" ? (
          <p className={LINE}>
            {REVIEW_COPY.rejected}{" "}
            <Link to="/assets" className={INLINE_LINK}>
              Back to Objects
            </Link>
          </p>
        ) : null}

        {reject.isError && state !== "review" ? (
          <ErrorLine error={reject.error} context="import" />
        ) : null}
      </div>

      {lightbox ? (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          label={`${lightbox.alt}, larger picture`}
          fit="contain"
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </>
  );
}
